import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCodeBranch,
  faCodeCommit,
  faDatabase,
  faRobot,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { Time } from './MomentTime';
import { isProduction } from '../Layout/utils';
import { privateInstance } from '../../DataAccessLayer/instances/private';
import { adminInfo } from '../../DataAccessLayer/services';

const getFormattedBuildTime = (buildTime) => {
  if (isUnavailableValue(buildTime)) return 'unavailable';

  const universityDate = Time.University(buildTime);
  if (!universityDate.isValid()) return buildTime;

  return universityDate.format('MMMM D, YYYY h:mm A');
};

const isUnavailableValue = (value) => {
  if (value === null || value === undefined) return true;

  const trimmedValue = String(value).trim();
  return !trimmedValue || trimmedValue.toLowerCase() === 'unknown';
};

const getBuildValue = (value) => (isUnavailableValue(value) ? 'unavailable' : String(value).trim());

const getCommitShortHash = (commitHash) => {
  if (isUnavailableValue(commitHash)) return 'unavailable';
  return String(commitHash).trim().slice(0, 7);
};

const BuildInfo = ({ className = '', hideOnProduction = false, loadRuntimeInfo = true }) => {
  const isHiddenOnProduction = hideOnProduction && isProduction();
  const [runtimeInfo, setRuntimeInfo] = useState({});

  useEffect(() => {
    if (!loadRuntimeInfo || isHiddenOnProduction) return;

    const getRuntimeInfo = async () => {
      try {
        const response = await privateInstance.get(adminInfo);
        setRuntimeInfo(response || {});
      } catch (error) {
        setRuntimeInfo({});
      }
    };

    getRuntimeInfo();
  }, [isHiddenOnProduction, loadRuntimeInfo]);

  if (isHiddenOnProduction) return null;

  const feBuildTime = getFormattedBuildTime(process.env.REACT_APP_BUILD_TIME);
  const feGitBranch = getBuildValue(process.env.REACT_APP_GIT_BRANCH);
  const feGitCommit = getCommitShortHash(process.env.REACT_APP_GIT_COMMIT);
  const beBuildTime = getFormattedBuildTime(runtimeInfo.buildTime);
  const beRunTime = getBuildValue(runtimeInfo.runTime);
  const beGitBranch = getBuildValue(runtimeInfo.gitBranch);
  const beGitCommit = getCommitShortHash(runtimeInfo.gitCommit);
  const currentUser = getBuildValue(runtimeInfo.currentUser);
  const trueUser = getBuildValue(runtimeInfo.trueUser);
  const databasePrefix = getBuildValue(runtimeInfo.databasePrefix);

  return (
    <Grid
      container
      className={`w-100 myOdu__buildInfo ${className}`.trim()}
      spacing={0}
      alignItems="stretch"
    >
      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faRobot} />
        <Typography component="h3" className="myOdu__smallest">
          FE Build Time
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          {feBuildTime}
        </Typography>
      </Grid>

      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faCodeBranch} />
        <Typography component="h3" className="myOdu__smallest">
          FE Branch/Commit
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          <span aria-label="front end git branch">&nbsp;{feGitBranch}&nbsp;</span>/
          <span aria-label="front end git commit">&nbsp;{feGitCommit}&nbsp;</span>
        </Typography>
      </Grid>
      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faDatabase} />
        <Typography component="h3" className="myOdu__smallest">
          Database Prefix
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          {databasePrefix}
        </Typography>
      </Grid>

      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faRobot} />
        <Typography component="h3" className="myOdu__smallest">
          BE Build/Run Time
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          {beBuildTime} / {beRunTime}
        </Typography>
      </Grid>
      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faCodeBranch} />
        <Typography component="h3" className="myOdu__smallest">
          BE Branch/Commit
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          <span aria-label="back end git branch">&nbsp;{beGitBranch}&nbsp;</span>/
          <span aria-label="back end git commit">&nbsp;{beGitCommit}&nbsp;</span>
        </Typography>
      </Grid>

      <Grid item xxl={4} lg={6} md={12} sm={12} xs={12}>
        <FontAwesomeIcon icon={faUser} />
        <Typography component="h3" className="myOdu__label myOdu__smallest pr-2">
          Current User/True User
        </Typography>
        <Typography component="span" className="myOdu__smallest">
          {currentUser}
          &nbsp;/&nbsp;
          {trueUser}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default BuildInfo;
